import { useDraggable, useDroppable } from '@dnd-kit/core';
import styled from 'styled-components';

import { FormLabel } from '@components/form-label';
import { IconFolder } from '@components/icons/folder';
import { IconSettings } from '@components/icons/settings';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { ToggleGroup } from '@components/toggle-group';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { SmallDragHandle } from '../drag-handle';
import { DropLine } from '../drop-line';
import { FieldEditHeader } from '../field-edit-header';
import { RenderField } from '../field-list';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { StateField } from '../state';

type Config = FieldConfig<'section'>;

type SectionStateField = Extract<StateField, { kind: 'section' }>;

const Display: Config['Display'] = ({ field }) => {
  return <EmptyContainer>{field.label}</EmptyContainer>;
};

const FormBuilderSection: Config['Component'] = ({ field, dragHandle }) => {
  const {
    store: { actions, useSelectedField },
  } = useFormBuilderContext();
  const selectedField = useSelectedField();
  const isSelected = selectedField?.id === field.id;
  const disallowDrag = field.builderProps?.canReorder === false;

  const topDropzone = useDroppable({
    id: `${field.id}-top`,
    data: { field },
  });

  const bottomDropzone = useDroppable({
    id: `${field.id}-bot`,
    data: {
      field,
      isSectionEnd: true,
    },
  });

  const sectionDrag = useDraggable({
    id: `selected-${field.id}`,
    data: { field },
    disabled: disallowDrag,
  });

  if (sectionDrag.isDragging) {
    return null;
  }

  const TopBorder = isSelected ? TopBorderSelected : TopBorderBase;
  const BottomBorder = isSelected ? BottomBorderSelected : BottomBorderBase;

  const hasFields = field.fields.length > 0;
  const isOneColumn = field.columns === 1;
  const isTwoColumn = !isOneColumn;
  const isOddCount = field.fields.length % 2 === 1;
  const isEvenCount = !isOddCount;
  const isDraggingChild = field.fields.indexOf(sectionDrag.active?.data.current?.field?.id) !== -1;
  const isDraggingFromOutside = !isDraggingChild;

  const showInlineExtraZone =
    hasFields &&
    isTwoColumn &&
    ((isOddCount && isDraggingFromOutside) || (isEvenCount && isDraggingChild));

  const showFixedBottomZone =
    hasFields &&
    (isOneColumn ||
      (isEvenCount && isDraggingFromOutside) ||
      (isTwoColumn && isOddCount && isDraggingChild));

  const isChildSelected = field.fields.some((id) => id === selectedField?.id);
  return (
    <>
      {topDropzone.isOver && <DropLine />}
      <Container
        id={`field-${field.id}`}
        ref={sectionDrag.setNodeRef}
        data-kind="section"
        data-selected={isSelected}
        {...(!isChildSelected && { ...sectionDrag.attributes, ...sectionDrag.listeners })}
        onClick={(e) => {
          e.stopPropagation();
          actions.selectField(field.id);
        }}
      >
        <TopDropzone ref={topDropzone.setNodeRef} />
        <TopBorder data-top-border="true">
          {isSelected ? (
            <FieldEditHeader
              field={field}
              content={
                <FieldEditHeader.Title
                  dragHandle={
                    !disallowDrag && (
                      <SmallDragHandle {...sectionDrag.listeners} {...sectionDrag.attributes} />
                    )
                  }
                  label={field.label}
                  icon={IconFolder}
                />
              }
            />
          ) : (
            <InactiveHeader>
              <IconFolder color="elementPurple60" />
              {field.label}
            </InactiveHeader>
          )}
        </TopBorder>

        <DisplayContainer
          data-columns={field.columns}
          onClick={() => {
            actions.selectField(field.id);
          }}
        >
          <FieldsList field={field} />

          {showInlineExtraZone && <InlineExtraDropzone field={field} />}

          {bottomDropzone.isOver && <DropLine />}

          {showFixedBottomZone && (
            <FixedPositionDropzone id="fixed-position-drop-zone" ref={bottomDropzone.setNodeRef} />
          )}
        </DisplayContainer>

        <BottomBorder data-bot-border="true" />
      </Container>
    </>
  );
};

const InlineExtraDropzone = ({ field }: { field: SectionStateField }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${field.id}-extra`,
    data: { field, isSectionEnd: true },
  });

  return <LastPositionDropzone ref={setNodeRef}>{isOver && <DropLine />}</LastPositionDropzone>;
};

const FieldsList = ({ field }: { field: SectionStateField }) => {
  const {
    store: { useFieldsFromIds },
  } = useFormBuilderContext();
  const fields = useFieldsFromIds(field.fields);

  if (field.fields.length === 0) {
    return <EmptyDropzone field={field} />;
  }

  return (
    <>
      {fields.map((innerField) => {
        return <RenderField key={innerField.id} field={innerField} />;
      })}
    </>
  );
};

const EmptyDropzone = ({ field }: { field: SectionStateField }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${field.id}-empty`,
    data: { field, isSectionEnd: true },
  });

  return (
    <EmptyFieldsDropzone ref={setNodeRef}>
      {isOver ? (
        <DropLine />
      ) : (
        <Text
          font="regular14"
          color="textHelper"
          text={t('emptyDragHint')}
          style={{ margin: 'auto' }}
        />
      )}
    </EmptyFieldsDropzone>
  );
};

const PropertiesSidebar = ({ field }: { field: SectionStateField }) => {
  const { store } = useFormBuilderContext();
  return (
    <SidebarContent>
      <Stack space={4}>
        <TextField
          label={t('sectionLabel')}
          defaultValue={field.label}
          onChange={store.actions.createChangeHandler(field, 'label')}
        />
        <div>
          <FormLabel text={t('columns')} />
          <ToggleGroup.Root
            fullWidth
            defaultValue={field.columns}
            onChange={(value) => {
              store.actions.createChangeHandler(field, 'columns')(value);
            }}
          >
            <ToggleGroup.Button text="1" variant="blue" value={1} />
            <ToggleGroup.Button text="2" variant="blue" value={2} />
          </ToggleGroup.Root>
        </div>
      </Stack>
    </SidebarContent>
  );
};

const sectionPropertiesTab = (field: SectionStateField) => {
  return {
    label: t('title'),
    icon: IconSettings,
    content: <PropertiesSidebar field={field} />,
  };
};

const { t } = getScopedTranslate({
  en: {
    title: 'Section',
    properties: 'Properties',
    sectionLabel: 'Section title',
    columns: 'Columns',
    emptyDragHint: 'No elements added',
  },
});

export const sectionConfig: Config = {
  kind: 'section',
  title: t('title'),
  group: 'structure',
  propertiesTabs: [sectionPropertiesTab, conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconFolder,
  Component: FormBuilderSection,
};

const DisplayContainer = styled.div({
  position: 'relative',
  display: 'grid',
  gap: pxToRem(8),
  '&[data-columns="1"]': {
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
  },
  '&[data-columns="2"]': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
});

const TopBorderBase = styled.div({
  padding: '0.5rem 1rem',
  backgroundImage: `linear-gradient(90deg, ${theme.color.backgroundPrimary}, ${theme.color.backgroundPrimary}), linear-gradient(180deg, ${theme.color.uiTrans20}, transparent)`,
  backgroundClip: 'padding-box, border-box',
  backgroundOrigin: 'border-box',
  border: `${pxToRem(2)} solid transparent`,
  borderRadius: pxToRem(4),
});

const TopBorderSelected = styled(TopBorderBase)({
  backgroundImage: `linear-gradient(90deg, ${theme.color.backgroundPrimary}, ${theme.color.backgroundPrimary}), linear-gradient(180deg, ${theme.color.elementBlue100}, transparent)`,
});

const BottomBorderBase = styled.div({
  background: 'transparent',
  padding: '0.5rem 1rem',
  backgroundImage: `linear-gradient(90deg, ${theme.color.backgroundPrimary}, ${theme.color.backgroundPrimary}), linear-gradient(0deg, ${theme.color.uiTrans20}, transparent 60%)`,
  backgroundClip: 'padding-box, border-box',
  backgroundOrigin: 'border-box',
  border: `${pxToRem(2)} solid transparent`,
  borderTop: 'none',
  borderRadius: `0 0 ${pxToRem(4)} ${pxToRem(4)}`,
});

const BottomBorderSelected = styled(BottomBorderBase)({
  backgroundImage: `linear-gradient(90deg, ${theme.color.backgroundPrimary}, ${theme.color.backgroundPrimary}), linear-gradient(0deg, ${theme.color.elementBlue100}, transparent 60%)`,
});

const SidebarContent = styled.div({
  padding: pxToRem(24),
});

const Container = styled.div({
  cursor: 'pointer',
  borderRadius: pxToRem(4),
  position: 'relative',
  border: `${pxToRem(1)} solid transparent`,
  padding: pxToRem(8),

  '[data-columns="2"] > &': {
    gridColumn: '1 / span 2',
  },

  '[data-kind="section"]': {
    margin: `0 ${pxToRem(8)}`,
  },
  '&:active': {
    cursor: 'grab',
  },
  '&:not(:has([data-selected="true"]))': {
    '&[data-selected="false"]:hover': {
      borderColor: theme.color.border10,
      background: theme.color.uiTrans5,

      '[data-top-border="true"]': {
        background: 'transparent',
      },

      '[data-bot-border="true"]': {
        background: 'transparent',
      },

      '[data-draggable="true"]': {
        cursor: 'grab',
      },
    },
  },
});

const EmptyContainer = styled.div<{ $isOver?: boolean }>((p) => ({
  background: 'transparent',
  border: `${pxToRem(1)} solid transparent`,
  borderRadius: pxToRem(4),
  padding: pxToRem(8),
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s ease-out, padding 0.15s ease-out',
  font: theme.font.regular14,
  color: theme.color.textHelper,
  height: pxToRem(40),

  ...(p.$isOver && {
    borderColor: theme.color.border10,
    background: theme.color.uiTrans5,
  }),
  '&:hover': {
    borderColor: theme.color.border10,
    background: theme.color.uiTrans5,
  },
}));

const TopDropzone = styled.div({
  position: 'absolute',
  left: 0,
  height: pxToRem(90),
  width: '100%',
  top: pxToRem(-10),
});

const InactiveHeader = styled.div({
  padding: `${pxToRem(8)} 0`,
  display: 'flex',
  alignItems: 'center',
  font: theme.font.regular14,
  color: theme.color.textSecondary,
  gap: pxToRem(8),
});

const EmptyFieldsDropzone = styled.div({
  width: '100%',
  height: pxToRem(40),
  padding: `0 ${pxToRem(16)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 'auto',
});

const FixedPositionDropzone = styled.div({
  position: 'absolute',
  width: '100%',
  height: pxToRem(32),
  bottom: pxToRem(-20),
  left: 0,
});

const LastPositionDropzone = styled.div({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
});
